import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { type ReactElement, useEffect } from 'react';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { Component } from './Component.tsx';
import {
  type MovieCertificationData,
  type TvCertificationData,
  formatCertifications,
} from './helpers/formatCertifications.ts';

const queries = {
  [ScreenType.Movie]: "\nquery GetMovieCertifications(\n  $id: ID!\n) {\n  movie(id: $id) {\n    ...MovieCertifications\n  }\n}\n\nfragment MovieCertifications on Movie {\n  releaseDates {\n    iso_3166_1\n    releaseDates {\n      certification\n    }\n  }\n}\n",
  [ScreenType.Tv]: "\nquery GetTvCertifications(\n  $id: ID!\n) {\n  tv(id: $id) {\n    ...TvCertifications\n  }\n}\n\nfragment TvCertifications on Tv {\n  contentRatings {\n    iso_3166_1\n    rating\n  }\n}\n",
};

export type QueryProps = {
  children?: (element: ReactElement) => ReactElement;
  id: string | number;
  type: ScreenType;
};

export const Query = ({ children, id, type }: QueryProps) => {
  const { config } = useToolbelt();
  const query = queries[type];

  const { execute, result } = useQuery<MovieCertificationData | TvCertificationData>(query, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id } }, { data: { initiator: 'CertificationIcon' } });
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const certification = formatCertifications(result?.data, config.country);

  if (!certification) {
    return;
  }

  return children ? (
    children(<Component certification={certification} id={id} type={type} />)
  ) : (
    <Component certification={certification} id={id} type={type} />
  );
};
